import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import { Box, Container, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { getCatalogById, getTableById } from '../services/catalogService';
import { Catalog } from '../models';

/**
 * CatalogRouteGuard - Validates catalog access before rendering child routes
 *
 * This component:
 * 1. Checks if catalog exists and is not hidden
 * 2. For routes with tableId, checks if orderTakingEnabled and table is active
 * 3. Redirects to catalog-only view if orderTaking is disabled or table is inactive
 * 4. Shows error page if catalog is hidden or not found
 */
const CatalogRouteGuard: React.FC = () => {
  const { catalogId, tableId } = useParams<{ catalogId: string; tableId?: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);

  useEffect(() => {
    const validateCatalog = async () => {
      if (!catalogId) {
        setError('No catalog ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch catalog data
        const catalogData = await getCatalogById(catalogId);

        if (!catalogData) {
          setError('Catalog not found');
          setLoading(false);
          return;
        }

        // Check if catalog is hidden
        if (catalogData.hidden === true) {
          setError('Catalog not found');
          setLoading(false);
          return;
        }

        // If accessing with tableId, validate table and order taking
        if (tableId) {
          // Check if order taking is enabled for this catalog
          if (catalogData.orderTakingEnabled === false) {
            // Redirect to catalog-only view (without tableId)
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/table\/[^/]+/, '');
            navigate(newPath, { replace: true });
            return;
          }

          // Check if table exists and is active
          const tableData = await getTableById(catalogId, tableId);

          if (!tableData) {
            setError('Table not found');
            setLoading(false);
            return;
          }

          // Check if table is active (default to true if not specified)
          if (tableData.active === false) {
            // Redirect to catalog-only view (without tableId)
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/table\/[^/]+/, '');
            navigate(newPath, { replace: true });
            return;
          }
        }

        // Validation passed
        setCatalog(catalogData);
        setLoading(false);
      } catch (err) {
        console.error('Error validating catalog:', err);
        setError('Failed to load catalog');
        setLoading(false);
      }
    };

    validateCatalog();
  }, [catalogId, tableId, navigate]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  // Error state
  if (error || !catalog) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: 'error.main',
                opacity: 0.1,
                margin: '0 auto',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="span"
                className="material-icons"
                sx={{
                  fontSize: 60,
                  color: 'error.main',
                }}
              >
                store
              </Box>
            </Box>

            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              Catalog Not Found
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              sx={{ mb: 1 }}
            >
              {error || 'The catalog you are looking for does not exist or has been removed.'}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Please check the URL or scan the QR code again.
            </Typography>

            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  minWidth: 140,
                  borderRadius: 2,
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  minWidth: 140,
                  borderRadius: 2,
                }}
              >
                Go to Home
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    );
  }

  // Validation passed - render child routes
  return <Outlet />;
};

export default CatalogRouteGuard;
